import type { RefObject } from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { useResizeObserver } from '@/hooks/useResizeObserver';

import { computeVisibleCount } from './overflow';

/** Default flex gap between items, in px (Tailwind `gap-0.5`). */
const DEFAULT_GAP = 2;
/** Never collapse below this many visible items. */
const DEFAULT_MIN_VISIBLE = 1;
/**
 * Footprint of the overflow (kebab) trigger INCLUDING the gap before it, in px.
 * The kebab is a fixed-size icon button, so a constant is robust enough; it is
 * only reserved when at least one item must be hidden.
 */
const DEFAULT_TRIGGER_WIDTH = 40;

export interface UseOverflowOptions {
  gap?: number;
  minVisible?: number;
  triggerWidth?: number;
  /** See {@link ComputeVisibleCountArgs.pinnedIndex}. */
  pinnedIndex?: number;
  /** See {@link ComputeVisibleCountArgs.reserve}. */
  reserve?: number;
  /**
   * Any value whose change means item widths may have changed without the
   * item count changing (e.g. a pill grew a badge). Triggers a re-measure;
   * item refs re-cache their widths on every render, so the measurement sees
   * the new sizes.
   */
  contentKey?: unknown;
}

export interface UseOverflowResult {
  /** Attach to the visible strip whose width is the available budget. */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Ref setter for each rendered item — caches its intrinsic width. */
  setItemRef: (index: number) => (el: HTMLElement | null) => void;
  /** How many leading items to render inline; the rest go to the menu. */
  visibleCount: number;
}

/**
 * Measure the available width of a strip and decide how many of its `itemCount`
 * items stay visible before the rest collapse into an overflow trigger.
 *
 * Strategy (DOM-light, loop-safe):
 *  - Start with everything visible so every item gets measured on first paint.
 *  - Cache each item's intrinsic width as it renders; overflowed items (not in
 *    the DOM) reuse their last-known width.
 *  - Recompute on container resize (ResizeObserver), after each visibleCount
 *    change, on item-set changes, and once web fonts settle.
 *  - Only call setState when the count actually changes, so measurement never
 *    drives an infinite render loop (see docs/claude/FRONTEND_PATTERNS.md).
 */
export function useOverflow(
  itemCount: number,
  options: UseOverflowOptions = {}
): UseOverflowResult {
  const {
    gap = DEFAULT_GAP,
    minVisible = DEFAULT_MIN_VISIBLE,
    triggerWidth = DEFAULT_TRIGGER_WIDTH,
    pinnedIndex,
    reserve = 0,
    contentKey,
  } = options;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemWidthsRef = useRef<number[]>([]);
  const [visibleCount, setVisibleCount] = useState(itemCount);

  const setItemRef = useCallback(
    (index: number) => (el: HTMLElement | null) => {
      if (el) {
        itemWidthsRef.current[index] = Math.ceil(
          el.getBoundingClientRect().width
        );
      }
    },
    []
  );

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    // Collect a complete width set; bail if any item hasn't been measured yet
    // (e.g. it's currently overflowed and has never rendered). The render-all
    // reset below guarantees a paint where every item is present.
    const widths: number[] = [];
    for (let i = 0; i < itemCount; i++) {
      const w = itemWidthsRef.current[i];
      if (typeof w !== 'number') {
        return;
      }
      widths.push(w);
    }
    const containerWidth = Math.round(container.clientWidth);
    const next = computeVisibleCount({
      containerWidth,
      itemWidths: widths,
      gap,
      triggerWidth,
      minVisible,
      pinnedIndex,
      reserve,
    });
    setVisibleCount(prev => (prev === next ? prev : next));
  }, [itemCount, gap, triggerWidth, minVisible, pinnedIndex, reserve]);

  useResizeObserver({ ref: containerRef, onResize: measure });

  // Measure on mount, when measurement inputs change, after each visibleCount
  // change (so a render-all pass is followed by a collapse pass), and when the
  // caller signals that item widths may have changed (contentKey).
  useLayoutEffect(() => {
    measure();
  }, [measure, visibleCount, contentKey]);

  // When the item set CHANGES (not on mount — that would fight the collapse
  // effect above and loop), render everything again so any new items get
  // measured; the effect above then collapses to the correct count.
  const prevItemCountRef = useRef(itemCount);
  useLayoutEffect(() => {
    if (prevItemCountRef.current !== itemCount) {
      prevItemCountRef.current = itemCount;
      setVisibleCount(itemCount);
    }
  }, [itemCount]);

  // Re-measure once web fonts settle — label widths can shift. Guarded for
  // environments without the Font Loading API (tests / SSR).
  useEffect(() => {
    const { fonts } = document as Document & { fonts?: FontFaceSet };
    if (!fonts?.ready) {
      return;
    }
    let cancelled = false;
    void fonts.ready.then(() => {
      if (!cancelled) {
        measure();
      }
    });
    return () => {
      cancelled = true;
    };
  }, [measure]);

  return { containerRef, setItemRef, visibleCount };
}
